import { axiosInstance } from './api'

/**
 * Legacy Reactions API Service
 *
 * Handles all API calls for the Legacy Reactions system.
 * Design: "Gathered, not counted."
 */

/**
 * Get all reactions for a specific piece of content.
 * @param {string} contentType - 'story' | 'memory' | 'milestone' | 'time_capsule'
 * @param {string|number} contentId - The ID of the content
 * @returns {Promise<{reactions: Array, my_reactions: Array, grouped: Object}>}
 */
export async function getReactions(contentType, contentId) {
  const response = await axiosInstance.get('/reactions/', {
    params: { content_type: contentType, content_id: contentId },
  })
  return response.data
}

/**
 * Toggle a reaction on/off (add if absent, remove if present).
 * @param {string} reactionType - One of the 6 legacy reaction types
 * @param {string} contentType - 'story' | 'memory' | 'milestone' | 'time_capsule'
 * @param {string|number} contentId - The ID of the content
 * @param {string} [note=''] - Optional note visible to family
 * @returns {Promise<{action: string, reaction: Object|null, my_reactions: Array}>}
 */
export async function toggleReaction(reactionType, contentType, contentId, note = '') {
  const response = await axiosInstance.post('/reactions/toggle/', {
    reaction_type: reactionType,
    content_type: contentType,
    content_id: String(contentId),
    note,
  })
  return response.data
}

/**
 * Update the note on an existing reaction.
 * @param {string} reactionId - UUID of the reaction
 * @param {string} note - New note text (max 500 chars)
 * @returns {Promise<Object>} Updated reaction
 */
export async function updateReactionNote(reactionId, note) {
  const response = await axiosInstance.patch(`/reactions/${reactionId}/note/`, {
    note,
  })
  return response.data
}

/**
 * Remove a specific reaction by ID.
 * @param {string} reactionId - UUID of the reaction
 */
export async function removeReaction(reactionId) {
  await axiosInstance.delete(`/reactions/${reactionId}/`)
}

// Send or retract love (idempotent toggle). Optional note attaches a keepsake.
export async function toggleLove(contentType, contentId, note = '') {
  return toggleReaction('love', contentType, contentId, note);
}

// One call powering the Notes-of-Love section.
export async function getStoryEngagement(storyId) {
  const { data } = await axiosInstance.get(`/lifeevent/events/${storyId}/engagement/`);
  return data;
}

// Post a keepsake note (a love reaction that carries text).
export async function sendNote(contentType, contentId, note) {
  return toggleReaction('love', contentType, contentId, note);
}
